<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { HUBPOST_CARS_ONBOARDING_EVENTS } from 'dashboard/helper/AnalyticsHelper/wgptEvents';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import OnboardingLayout from '../../../shared/OnboardingLayout.vue';
import OnboardingSection from '../../../shared/OnboardingSection.vue';
import OnboardingFormRow from '../../../account-details/OnboardingFormRow.vue';
import OnboardingFormSelect from '../../../account-details/OnboardingFormSelect.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountId, currentAccount, finishOnboarding } = useAccount();

// Where each onboarding cursor routes. The backend owns which steps run where;
// the frontend just follows the cursor it advanced us to.
const STEP_ROUTES = {
  wgpt_hubpost_cars_showroom_setup:
    'onboarding_wgpt_hubpost_cars_showroom_setup',
};

// The language the employee speaks to customers in. Deliberately separate from
// the dashboard locale — a dealer can run their dashboard in English and still
// sell to Arabic-speaking customers. Same set as the storefront offers.
const LANGUAGES = ['en', 'ar'];

// Every market HubPost sells into today is Arabic-speaking, so the employee
// starts out speaking Arabic in that market's own dialect. Both fields stay
// editable: the region is where the dealer pays from, which is a good guess at
// who they sell to, not an answer.
//
// The dialect is English on purpose — it is interpolated into the employee's
// English prompt ("in a natural {DIALECT} dialect"), never shown to a customer.
const REGION_DEFAULTS = {
  SA: { language: 'ar', dialect: 'Saudi' },
  AE: { language: 'ar', dialect: 'Emirati' },
  KW: { language: 'ar', dialect: 'Kuwaiti' },
  QA: { language: 'ar', dialect: 'Qatari' },
  BH: { language: 'ar', dialect: 'Bahraini' },
  OM: { language: 'ar', dialect: 'Omani' },
  EG: { language: 'ar', dialect: 'Egyptian' },
  JO: { language: 'ar', dialect: 'Jordanian' },
};

const currentUser = useMapGetter('getCurrentUser');

// The market the dealer signed up for. Carried on both step events so the
// funnel can be read per market, which is how HubPost is priced and sold.
const region = computed(() => currentAccount.value?.wgpt_attributes?.region);
const regionDefaults = computed(() => REGION_DEFAULTS[region.value]);

const displayName = ref('');
const language = ref(
  regionDefaults.value?.language ||
    (LANGUAGES.includes(currentAccount.value?.locale)
      ? currentAccount.value.locale
      : 'en')
);
const dialect = ref(regionDefaults.value?.dialect || '');
const workingHours = ref('');
const locations = ref('');

const isSubmitting = ref(false);
const showErrorOnFields = ref(false);

// Every parameter hubpost_bot declares is required — a blank one is dropped
// before interpolation and leaves a hole in the employee's prompt. Language is
// a select that always holds a value, so it needs no rule.
const validationRules = {
  displayName: { required },
  dialect: { required },
  workingHours: { required },
  locations: { required },
};

const v$ = useVuelidate(validationRules, {
  displayName,
  dialect,
  workingHours,
  locations,
});

const userName = computed(() => currentUser.value?.name || '');
const userEmail = computed(() => currentUser.value?.email || '');
const accountName = computed(() => currentAccount.value?.name || '');

const languageOptions = computed(() =>
  LANGUAGES.map(code => ({
    value: code,
    label: t(`WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.LANGUAGES.${code}`),
  }))
);

onMounted(() => {
  useTrack(HUBPOST_CARS_ONBOARDING_EVENTS.ACCOUNT_DETAILS_VISITED, {
    region: region.value,
  });
});

// A toast alone doesn't say *which* field is missing. Fields keep a red outline
// once touched-and-invalid, and shake once on a rejected submit.
const fieldClass = field => [
  'w-auto text-end px-1 py-0.5 -my-0.5 mx-0 placeholder:text-n-slate-9 rounded',
  {
    'ring-1 ring-n-ruby-8': v$.value[field].$error,
    'animate-shake': showErrorOnFields.value && v$.value[field].$error,
  },
];

// The textarea carries its error state on the wrapper, not the input itself.
const locationsWrapperClass = computed(() =>
  showErrorOnFields.value && v$.value.locations.$error ? 'animate-shake' : ''
);

const flagMissingFields = () => {
  showErrorOnFields.value = true;
  setTimeout(() => {
    showErrorOnFields.value = false;
  }, 600);
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;

  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.VALIDATION_ERROR'));
    flagMissingFields();
    return;
  }

  isSubmitting.value = true;
  try {
    await finishOnboarding({
      onboarding_step: 'wgpt_hubpost_cars_account_details',
      ai_employee: {
        display_name: displayName.value,
        language: language.value,
        dialect: dialect.value,
        working_hours: workingHours.value,
        locations: locations.value,
      },
    });

    // Only after the step actually persisted, and only categorical values —
    // the free-text fields are the dealer's own business copy.
    useTrack(HUBPOST_CARS_ONBOARDING_EVENTS.ACCOUNT_DETAILS_COMPLETED, {
      region: region.value,
      language: language.value,
    });

    useAlert(t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.SUCCESS'));
    // Follow the cursor the backend advanced us to. No next step means
    // onboarding is done, so refresh the user and head to the dashboard.
    const nextStep = currentAccount.value?.custom_attributes?.onboarding_step;
    if (STEP_ROUTES[nextStep]) {
      router.push({
        name: STEP_ROUTES[nextStep],
        params: { accountId: accountId.value },
      });
    } else {
      await store.dispatch('setUser');
      router.push({ name: 'home', params: { accountId: accountId.value } });
    }
  } catch (error) {
    useAlert(error?.message || t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <OnboardingLayout
      :greeting="
        t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.GREETING', { name: userName })
      "
      :subtitle="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.SUBTITLE')"
      :continue-label="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.CONTINUE')"
      :is-loading="isSubmitting"
    >
      <OnboardingSection
        :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.YOUR_DETAILS')"
        icon="i-lucide-user"
      >
        <div class="flex items-center gap-2 px-3 py-3">
          <Avatar :name="userName" :size="16" rounded-full />
          <span class="text-sm font-medium text-n-slate-12">
            {{ userName }}
          </span>
        </div>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.FIELDS.EMAIL')"
          icon="i-lucide-mail"
        >
          <div class="flex items-center justify-end gap-1.5">
            <span class="text-sm text-n-slate-12">{{ userEmail }}</span>
            <Icon
              v-tooltip="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.EMAIL_VERIFIED')"
              icon="i-lucide-circle-check"
              class="flex-shrink-0 size-4 text-n-teal-11"
            />
          </div>
        </OnboardingFormRow>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.FIELDS.DEALERSHIP')"
          icon="i-lucide-store"
        >
          <span class="text-sm text-n-slate-12">{{ accountName }}</span>
        </OnboardingFormRow>
      </OnboardingSection>

      <OnboardingSection
        :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.TITLE')"
        icon="i-lucide-bot"
      >
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.NAME')"
          icon="i-lucide-user"
        >
          <InlineInput
            v-model="displayName"
            :placeholder="
              t(
                'WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.NAME_PLACEHOLDER'
              )
            "
            :custom-input-class="fieldClass('displayName')"
            @blur="v$.displayName.$touch()"
          />
        </OnboardingFormRow>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.LANGUAGE')"
          icon="i-lucide-languages"
        >
          <OnboardingFormSelect v-model="language" :options="languageOptions" />
        </OnboardingFormRow>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.DIALECT')"
          icon="i-lucide-messages-square"
        >
          <InlineInput
            v-model="dialect"
            :placeholder="
              t(
                'WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.DIALECT_PLACEHOLDER'
              )
            "
            :custom-input-class="fieldClass('dialect')"
            @blur="v$.dialect.$touch()"
          />
        </OnboardingFormRow>
        <OnboardingFormRow
          :title="
            t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.WORKING_HOURS')
          "
          icon="i-lucide-clock"
        >
          <InlineInput
            v-model="workingHours"
            :placeholder="
              t(
                'WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.HOURS_PLACEHOLDER'
              )
            "
            :custom-input-class="fieldClass('workingHours')"
            @blur="v$.workingHours.$touch()"
          />
        </OnboardingFormRow>
        <!-- One line per branch with its Maps link, so this field owns a full
             row rather than the narrow value column the others share. -->
        <div class="flex flex-col gap-2 px-3 py-3 border-t border-n-weak">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-map-pin"
              class="size-4 text-n-slate-9 flex-shrink-0"
            />
            <span class="text-n-slate-11">
              {{ t('WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.LOCATIONS') }}
            </span>
          </div>
          <TextArea
            v-model="locations"
            auto-height
            min-height="4.5rem"
            :placeholder="
              t(
                'WGPT_HUBPOST_CARS_ACCOUNT_DETAILS.AI_EMPLOYEE.LOCATIONS_PLACEHOLDER'
              )
            "
            :message-type="v$.locations.$error ? 'error' : 'info'"
            :custom-text-area-wrapper-class="locationsWrapperClass"
            @blur="v$.locations.$touch()"
          />
        </div>
      </OnboardingSection>
    </OnboardingLayout>
  </form>
</template>
